import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiCall from '../../../api';
import { notify } from '../../helper/notify';
import i18n from '../../../translation/i18n.js';

// Types
interface PersonalData {
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    incomeTax: number;
    phoneNo: string;
    ibanNumber: string;
    bicNumber: string;
    bbanNumber: string;
    isBriteIdExist: boolean;
    isBriteSessionAccount: boolean;
    bankAccounts: Array<{
        bank: string;
        clearingNumber: string;
        accountNumber: string;
    }>;
    isVerified: boolean;
    dateOfBirth: string;
    bank: string;
    clearingNumber: string;
    accountNumber: string;
    customCheck: boolean;
    accountType: number;
    ssn: string;
    profession: string;
    country: string;
    city: string;
    address: string;
    zipCode: string;
    profileImage: string;
    createdDate: string;
}

interface TaxInformation {
    id: number;
    newTaxValue: number;
    oldTaxValue: number
    requestStatus: string;
    userId: number;
    createdDate: string;
    updatedDate: string | null;
}

interface UserPreferences {
    id: number;
    userId: number;
    organizationName: string;
    logo: string | null;
    currencies: string[] | [];
    preferredLanguage: string;
    createdDate: string;
    updatedDate: string | null;
    themeColors: Record<string, string> | null;
    logoWidth: number | null;
    logoHeight: number | null;
}

interface InvoiceThemePreset {
    key: string;
    nameEn: string;
    nameSv: string;
    themeColors: Record<string, string>;
}

interface ProfileSettingsState {
    personalData: PersonalData;
    taxInformation: TaxInformation | null;
    userPreferences: UserPreferences | null;
    isError: boolean;
    isLoading: boolean;
    message: string;
    isSuccess: boolean;
    templatePreviewUrl: string | null;
    templatePreviewLoading: boolean;
    // Tracks the most recently *dispatched* preview request so a slower, older
    // response (e.g. the very first preview fired on mount, before the user's
    // saved theme has even loaded) can't land after a newer one and overwrite
    // it with stale colors - network responses aren't guaranteed to resolve in
    // the order they were sent.
    latestPreviewRequestId: string | null;
    themePresets: InvoiceThemePreset[];
}

interface UpdatePersonalDataPayload {
    userData: Partial<PersonalData>;
}
const t = (key: string): string => String(i18n.t(key));

const initialState: ProfileSettingsState = {
    personalData: {
        firstName: '',
        lastName: '',
        fullName: '',
        email: '',
        phoneNo: '',
        ssn: '',
        incomeTax: 0,
        ibanNumber: '',
        bicNumber: '',
        bbanNumber: '',
        isBriteIdExist: false,
        isBriteSessionAccount: false,
        bankAccounts: [],
        customCheck: false,
        isVerified: false,
        bank: '',
        clearingNumber: '',
        accountNumber: '',
        accountType: 0,
        dateOfBirth: '',
        profession: '',
        country: '',
        city: '',
        address: '',
        zipCode: '',
        profileImage: '',
        createdDate: ''
    },
    taxInformation: null,
    userPreferences: null,
    isError: false,
    isLoading: false,
    message: '',
    isSuccess: false,
    templatePreviewUrl: null,
    templatePreviewLoading: false,
    latestPreviewRequestId: null,
    themePresets: [],
};

/** Malformed/absent JSON just means "no overrides" - never worth surfacing as an error. */
const parseThemeColorsJson = (json: unknown): Record<string, string> | null => {
    if (typeof json !== 'string' || !json.trim()) return null;
    try {
        return JSON.parse(json);
    } catch {
        return null;
    }
};

// Async thunk for fetching personal data
export const fetchPersonalData = createAsyncThunk(
    'profileSettings/fetchPersonalData',
    async (userId: string, thunkAPI) => {
        try {
            if (!userId) {
                return thunkAPI.rejectWithValue('User ID is required');
            }

            const res = await apiCall.get(`/employee/get/${userId}`);

            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to fetch personal data:', message);
            notify.error(error?.response?.data?.message || t('PROFILE_DATA_LOAD_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Async thunk for updating personal data
export const updatePersonalData = createAsyncThunk(
    'profileSettings/updatePersonalData',
    async ({ userData }: UpdatePersonalDataPayload, thunkAPI) => {
        try {
            const res = await apiCall.put(`/employee/update`, userData);

            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            notify.success(t('PROFILE_DATA_UPDATED'));
            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to update personal data:', message);
            notify.error(error?.response?.data?.message || t('PROFILE_DATA_UPDATE_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Async thunk for updating bank data
export const updateBankData = createAsyncThunk(
    'profileSettings/updateBankData',
    async ({ userId, bankData }: { userId: string; bankData: { bank: string; clearingNumber?: string; accountNumber?: string; ibanNumber?: string; bicNumber?: string; bbanNumber?: string; code?: string; } }, thunkAPI) => {
        try {
            console.log('Updating bank data with:', bankData);
            if (!userId) {
                return thunkAPI.rejectWithValue('User ID is required');
            }

            const res = await apiCall.put(`/settings/save-customer-salary`, bankData);

            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to update bank data:', message);
            // notify.error(t('BANK_DETAILS_UPDATE_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const removeBankAccount = createAsyncThunk(
    'profileSettings/removeBankAccount',
    async (_, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const res = await apiCall.delete(`/employee/bank-details`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            notify.success(t('BANK_ACCOUNT_REMOVED'));
            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to remove bank account:', message);
            notify.error(error?.response?.data?.message || t('BANK_ACCOUNT_REMOVE_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Async thunk for fetching tax information
export const fetchTaxInformation = createAsyncThunk(
    'profileSettings/fetchTaxInformation',
    async (_, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const res = await apiCall.get(`/incometax`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'accept': '*/*'
                }
            });
            console.log('Tax information response:', res.data);
            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to fetch tax information:', message);
            notify.error(error?.response?.data?.message || t('TAX_INFORMATION_LOAD_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Async thunk for fetching user preferences
export const fetchUserPreferences = createAsyncThunk(
    'profileSettings/fetchUserPreferences',
    async (args: { silent?: boolean } | undefined, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const res = await apiCall.get(`/userpreference`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'accept': '*/*'
                }
            });

            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to fetch user preferences:', message);
            if (!args?.silent) {
                notify.error(error?.response?.data?.message || t('USER_PREFERENCES_LOAD_FAILED'));
            }
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Async thunk for updating user preferences
export const updateUserPreferences = createAsyncThunk(
    'profileSettings/updateUserPreferences',
    async ({ organizationName, logo, removeLogo, currencies, themeColors, logoWidth, logoHeight, silent }: {
        organizationName: string;
        logo?: File;
        // Explicitly clears the currently saved logo. Ignored when `logo` is also
        // set - a newly chosen file always takes precedence over a removal request.
        removeLogo?: boolean;
        currencies: string[];
        themeColors?: Record<string, string>;
        logoWidth?: number;
        logoHeight?: number;
        // The Preferences tab autosaves on every edit - a blocking "Saved!" (or
        // "Failed!") modal firing every ~900ms while someone is mid-edit would make
        // the page unusable, so autosave callers pass silent:true and rely on the
        // inline status indicator instead.
        silent?: boolean;
    }, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();

            formData.append('OrganizationName', organizationName);

            if (logo) {
                formData.append('Logo', logo);
            } else if (removeLogo) {
                formData.append('RemoveLogo', 'true');
            }

            // Append each currency separately
            currencies.forEach(currency => {
                formData.append('Currency', currency);
            });

            if (themeColors && Object.keys(themeColors).length > 0) {
                formData.append('ThemeColorsJson', JSON.stringify(themeColors));
            }
            if (logoWidth != null) {
                formData.append('LogoWidth', String(logoWidth));
            }
            if (logoHeight != null) {
                formData.append('LogoHeight', String(logoHeight));
            }

            const res = await apiCall.post(`/userpreference`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'accept': '*/*',
                    // Do NOT set Content-Type here - axios needs to compute it itself
                    // for a FormData body so it can append the multipart boundary
                    // (e.g. "multipart/form-data; boundary=----WebKitFormBoundary...").
                    // A hardcoded 'multipart/form-data' with no boundary overrides
                    // that, so the backend can't parse the body and every save here
                    // (org name, logo, currencies, theme colors, logo size) silently
                    // fails.
                }
            });

            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            if (!silent) {
                notify.success(t('PREFERENCES_SAVED'));
            }
            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to update user preferences:', message);
            if (!silent) {
                notify.error(error?.response?.data?.message || t('PREFERENCES_SAVE_FAILED'));
            }
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Renders a sample invoice PDF styled with the *candidate* (not-yet-saved) template
// settings, so the Preferences page can show a live preview before the user hits Save.
export const previewInvoiceTemplate = createAsyncThunk(
    'profileSettings/previewInvoiceTemplate',
    async ({ themeColors, logoWidth, logoHeight, locale }: {
        themeColors?: Record<string, string>;
        logoWidth?: number;
        logoHeight?: number;
        locale?: string;
    }, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const res = await apiCall.post(
                `/InvoiceTemplatePreviewV2/preview`,
                { themeColors, logoWidth, logoHeight, locale },
                {
                    responseType: 'blob',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        Accept: 'application/pdf',
                    },
                }
            );

            // Converted to an object URL here rather than in the reducer so the
            // dispatched action only ever carries a plain string - a raw Blob as
            // a payload trips Redux's non-serializable-value warning.
            return URL.createObjectURL(res.data as Blob);
        } catch (error: any) {
            const message = error?.message || 'Failed to render invoice template preview';
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Curated color-combination presets (named in English + Swedish) the user can pick
// from instead of hand-picking every color - fetched from the backend so the palette
// and its names live in one place (Invozio.PdfGenerator/Theming defaults stay in sync).
export const fetchInvoiceThemePresets = createAsyncThunk(
    'profileSettings/fetchInvoiceThemePresets',
    async (_, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const res = await apiCall.get(`/InvoiceTemplatePreviewV2/presets`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            return res.data as Array<{ key: string; nameEn: string; nameSv: string; themeColors: Record<string, string> }>;
        } catch (error: any) {
            const message = error?.message || 'Failed to load invoice theme presets';
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Async thunk for updating tax information
export const updateTaxInformation = createAsyncThunk(
    'profileSettings/updateTaxInformation',
    async ({ taxPercentage, file, language = 'sv-se' }: { taxPercentage: number; file?: File; language?: string }, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();

            if (file) {
                formData.append('file', file);
            }

            const res = await apiCall.post(`/incometax?taxPercentage=${taxPercentage}&language=${language}`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'accept': '*/*',
                    'Content-Type': 'multipart/form-data'
                }
            });
            // Handle specific error for unfinished request
            if (
                res.data.statusCode === 400 &&
                res.data.message === "Your last request hasn’t finished yet. Once it’s complete, you can submit a new one."
            ) {
                // Use translation keys for both languages
                const errorKey = 'TAX_REQUEST_UNFINISHED';
                notify.error(i18n.t(errorKey));
                return thunkAPI.rejectWithValue(res.data.message);
            }
            if (
                res.data.statusCode === 400 &&
                res.data.message === "To make a tax deduction, please upload the required file and try again!"
            ) {
                console.log('Triggering toast for missing file requirement');
                notify.error(t('TAX_FILE_REQUIRED'));
            }

            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            notify.success(t('TAX_CHANGE_REQUEST_SUBMITTED'));
            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to update tax information:', message);
            notify.error(error?.response?.data?.message || t('TAX_CHANGE_REQUEST_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Async thunk for canceling tax request
export const cancelTaxRequest = createAsyncThunk(
    'profileSettings/cancelTaxRequest',
    async (_, thunkAPI) => {
        try {
            const token = localStorage.getItem('token');
            const res = await apiCall.delete(`/incometax`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'accept': '*/*'
                }
            });

            if (!(res.data.statusCode === 200 || res.data.statusCode === 201)) {
                return thunkAPI.rejectWithValue(res.data.message);
            }

            notify.success(t('TAX_REQUEST_CANCELLED_SUCCESSFULLY'));
            return res.data.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();

            console.error('Failed to cancel tax request:', message);
            notify.error(message || t('TAX_CANCEL_FAILED'));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

const profileSettingsSlice = createSlice({
    name: 'profileSettings',
    initialState,
    reducers: {
        reset: (state) => {
            state.isLoading = false;
            state.isError = false;
            state.isSuccess = false;
            state.message = '';
        },
        resetMessages: (state) => {
            state.isError = false;
            state.isSuccess = false;
            state.message = '';
        },
        updatePersonalDataLocally: (state, action) => {
            state.personalData = { ...state.personalData, ...action.payload };
        },
        setPersonalData: (state, action) => {
            state.personalData = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Personal Data
            .addCase(fetchPersonalData.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(fetchPersonalData.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.personalData = {
                    firstName: action.payload?.firstName || '',
                    lastName: action.payload?.lastName || '',
                    fullName: action.payload?.fullName || '',
                    email: action.payload?.email || '',
                    phoneNo: action.payload?.phoneNo || '',
                    ssn: action.payload?.ssn || '',
                    incomeTax: action.payload?.incomeTax,
                    bicNumber: action.payload?.customerSalaryInformation?.[0]?.bicNumber || '',
                    ibanNumber: action.payload?.customerSalaryInformation?.[0]?.ibanNumber || '',
                    bbanNumber: action.payload?.customerSalaryInformation?.[0]?.bbanNumber || '',
                    isBriteIdExist: action.payload?.isBriteIdExist || false,
                    isBriteSessionAccount: action.payload?.isBriteSessionAccount !== false,
                    bankAccounts: action.payload?.customerSalaryInformation || [],
                    bank: action.payload?.customerSalaryInformation?.[0]?.bank || '',
                    clearingNumber: action.payload?.customerSalaryInformation?.[0]?.clearingNumber || '',
                    accountNumber: action.payload?.customerSalaryInformation?.[0]?.accountNumber || '',
                    isVerified: action.payload?.isVerified || false,
                    dateOfBirth: action.payload?.dateOfBirth || '',
                    accountType: action.payload?.accountType || 0,
                    profession: action.payload?.profession || '',
                    country: action.payload?.country || '',
                    customCheck: action.payload?.customCheck || false,
                    city: action.payload?.city || '',
                    address: action.payload?.address || '',
                    zipCode: action.payload?.zipCode || '',
                    profileImage: action.payload?.profileImage || '',
                    createdDate: action.payload?.createdDate || ''
                };
                console.log(state.personalData, 'state personal Data')
            })
            .addCase(fetchPersonalData.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })
            // Update Personal Data
            .addCase(updatePersonalData.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(updatePersonalData.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.personalData = { ...state.personalData, ...action.payload };
            })
            .addCase(updatePersonalData.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })
            .addCase(updateBankData.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(updateBankData.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.personalData.bank = action.payload?.bank || state.personalData.bank;
                state.personalData.clearingNumber = action.payload?.clearingNumber || state.personalData.clearingNumber;
                state.personalData.accountNumber = action.payload?.accountNumber || state.personalData.accountNumber;
                notify.success(t('BANK_DETAILS_UPDATED'));
            })
            .addCase(updateBankData.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
                notify.error(t('BANK_DETAILS_UPDATE_FAILED'));
            })
            .addCase(removeBankAccount.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(removeBankAccount.fulfilled, (state) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.personalData.bank = '';
                state.personalData.clearingNumber = '';
                state.personalData.accountNumber = '';
                state.personalData.ibanNumber = '';
                state.personalData.bicNumber = '';
                state.personalData.bbanNumber = '';
                state.personalData.isBriteIdExist = false;
                state.personalData.isBriteSessionAccount = false;
                state.personalData.bankAccounts = [];
            })
            .addCase(removeBankAccount.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })
            // Fetch Tax Information
            .addCase(fetchTaxInformation.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(fetchTaxInformation.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.taxInformation = {
                    id: action.payload?.id || 0,
                    // 0 is a valid tax rate, so keep it — only null/undefined defaults to 0.
                    newTaxValue: action.payload?.newTaxValue ?? 0,
                    oldTaxValue: action.payload?.oldTaxValue ?? 0,
                    requestStatus: action.payload?.requestStatus || '',
                    userId: action.payload?.userId || 0,
                    createdDate: action.payload?.createdDate || '',
                    updatedDate: action.payload?.updatedDate || null
                };
                state.message = action.payload.message || '';
            })
            .addCase(fetchTaxInformation.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })
            // Update Tax Information
            .addCase(updateTaxInformation.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(updateTaxInformation.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.taxInformation = {
                    id: action.payload?.id || 0,
                    newTaxValue: action.payload?.newTaxValue ?? 0,
                    oldTaxValue: action.payload?.oldTaxValue ?? 0,
                    requestStatus: action.payload?.requestStatus ?? null,
                    userId: action.payload?.userId || 0,
                    createdDate: action.payload?.createdDate || '',
                    updatedDate: action.payload?.updatedDate || null
                };
            })
            .addCase(updateTaxInformation.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })
            // Cancel Tax Request
            .addCase(cancelTaxRequest.pending, (state) => {
                state.isLoading = true;
                state.isError = false;
                state.message = '';
            })
            .addCase(cancelTaxRequest.fulfilled, (state) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.taxInformation = null;
            })
            .addCase(cancelTaxRequest.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })
            // Fetch User Preferences
            .addCase(fetchUserPreferences.pending, (state, action) => {
                // A silent refetch (e.g. picking up the server-assigned logo filename
                // right after an autosave) must not gate the page behind the skeleton.
                if (!action.meta.arg?.silent) {
                    state.isLoading = true;
                }
                state.isError = false;
                state.message = '';
            })
            .addCase(fetchUserPreferences.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.userPreferences = {
                    id: action.payload?.id || 0,
                    userId: action.payload?.userId || 0,
                    organizationName: action.payload?.organizationName || '',
                    logo: action.payload?.logo || null,
                    currencies: action.payload?.currencies || [],
                    preferredLanguage: action.payload?.preferredLanguage || '',
                    createdDate: action.payload?.createdDate || '',
                    updatedDate: action.payload?.updatedDate || null,
                    themeColors: parseThemeColorsJson(action.payload?.themeColorsJson),
                    logoWidth: action.payload?.logoWidth ?? null,
                    logoHeight: action.payload?.logoHeight ?? null
                };
            })
            .addCase(fetchUserPreferences.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })
            // Update User Preferences
            .addCase(updateUserPreferences.pending, (state, action) => {
                // Autosave (silent: true) must not gate the whole page behind the
                // full-page skeleton below - only an explicit, user-initiated save
                // (none left today, but kept for any future one) should.
                if (!action.meta.arg?.silent) {
                    state.isLoading = true;
                }
                state.isError = false;
                state.message = '';
            })
            .addCase(updateUserPreferences.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                // The save response (SignUpUserViewModel) doesn't echo back
                // organizationName/currencies/themeColors/logo size at all - it's a
                // generic user DTO, not a preferences one. Trust what we just sent
                // (action.meta.arg) for those instead of the response, which would
                // otherwise silently wipe them back to blank on every single save.
                const sent = action.meta.arg;
                state.userPreferences = {
                    id: action.payload?.id || state.userPreferences?.id || 0,
                    userId: action.payload?.userId || state.userPreferences?.userId || 0,
                    organizationName: sent.organizationName ?? state.userPreferences?.organizationName ?? '',
                    logo: sent.removeLogo ? null : (action.payload?.logo || state.userPreferences?.logo || null),
                    currencies: sent.currencies ?? state.userPreferences?.currencies ?? [],
                    preferredLanguage: action.payload?.preferredLanguage || state.userPreferences?.preferredLanguage || '',
                    createdDate: action.payload?.createdDate || state.userPreferences?.createdDate || '',
                    updatedDate: action.payload?.updatedDate || null,
                    themeColors: sent.themeColors ?? state.userPreferences?.themeColors ?? null,
                    logoWidth: sent.logoWidth ?? state.userPreferences?.logoWidth ?? null,
                    logoHeight: sent.logoHeight ?? state.userPreferences?.logoHeight ?? null
                };
            })
            .addCase(updateUserPreferences.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload as string;
            })
            // Live invoice-template preview (candidate, not-yet-saved settings)
            .addCase(previewInvoiceTemplate.pending, (state, action) => {
                state.templatePreviewLoading = true;
                state.latestPreviewRequestId = action.meta.requestId;
            })
            .addCase(previewInvoiceTemplate.fulfilled, (state, action) => {
                // A newer preview has been dispatched since this one went out (e.g.
                // this was the very first preview, fired before the user's saved
                // theme even finished loading) - its response is stale, discard it
                // rather than let it clobber a more recent, correct one.
                if (action.meta.requestId !== state.latestPreviewRequestId) {
                    return;
                }
                state.templatePreviewLoading = false;
                if (state.templatePreviewUrl) {
                    URL.revokeObjectURL(state.templatePreviewUrl);
                }
                state.templatePreviewUrl = action.payload;
            })
            .addCase(previewInvoiceTemplate.rejected, (state, action) => {
                if (action.meta.requestId !== state.latestPreviewRequestId) {
                    return;
                }
                state.templatePreviewLoading = false;
            })
            .addCase(fetchInvoiceThemePresets.fulfilled, (state, action) => {
                state.themePresets = action.payload || [];
            })
            .addCase(fetchInvoiceThemePresets.rejected, (state) => {
                state.themePresets = [];
            });
    },
});

export const {
    reset,
    resetMessages,
    updatePersonalDataLocally,
    setPersonalData
} = profileSettingsSlice.actions;

export default profileSettingsSlice.reducer;

// Export types for use in components
export type { PersonalData, ProfileSettingsState, UpdatePersonalDataPayload, TaxInformation, UserPreferences };